import { useState } from "react";
import { ShieldCheck, ShieldAlert, Sparkles, Baby, ChevronDown, ChevronUp, AlertTriangle, Info } from "lucide-react";
import { ProductStatus } from "@/components/ProductStatus";

const LEVEL_COLOR = {
  safe: "#166534",
  low_risk: "#166534",
  moderate: "#92400E",
  high_risk: "#B91C1C",
  hazardous: "#B91C1C",
};
const LEVEL_TONE = { safe: "success", low_risk: "success", moderate: "warning", high_risk: "danger", hazardous: "danger" };
const LEVEL_LABEL = {
  safe: "Sicuro",
  low_risk: "Rischio basso",
  moderate: "Moderato",
  high_risk: "Rischio alto",
  hazardous: "Pericoloso",
};
const FIT_COLOR = {
  good: "#166534",
  neutral: "#475569",
  bad: "#B91C1C",
};
const FIT_TONE = { good: "success", neutral: "neutral", bad: "danger" };
const FIT_LABEL = { good: "Ok", neutral: "Neutro", bad: "Sconsigliato" };
const SKIN_LABEL = {
  oily: "Grassa", dry: "Secca", sensitive: "Sensibile",
  combination: "Mista", normal: "Normale", acneProne: "Acneica",
};

function Score({ label, value, suffix = "", color = "#1A1A1A", testId }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-luxury text-[#707070]">{label}</div>
      <div className="text-2xl font-serif" style={{ color }}>
        {value ?? "—"}{value != null && suffix}
      </div>
    </div>
  );
}

/**
 * Compact analysis panel for INCI safety data.
 * Shows overall score, badges (pregnancy/clean beauty/comedogenicity),
 * skin compatibility matrix, allergen flags, and an expandable per-ingredient table.
 */
export default function InciAnalysisPanel({ analysis, updatedAt, onRefresh, busy, compact = false }) {
  const [expanded, setExpanded] = useState(false);

  if (!analysis) {
    return (
      <div className="luxury-card p-4 text-sm text-[#707070] flex items-center justify-between gap-3" data-testid="inci-analysis-empty">
        <span>Analisi INCI non ancora disponibile.</span>
        {onRefresh && (
          <button onClick={onRefresh} disabled={busy}
                  className="btn-ghost text-xs" data-testid="inci-analysis-run-btn">
            {busy ? "Analisi..." : "Esegui analisi"}
          </button>
        )}
      </div>
    );
  }

  const level = analysis.safetyLevel || "moderate";
  const levelColor = LEVEL_COLOR[level] || "#1A1A1A";
  const overall = analysis.overallSafetyScore;
  const clean = analysis.cleanBeautyScore;
  const comedo = analysis.comedogenicityScore;
  const coverage = analysis.coverage;
  const pregSafe = analysis.pregnancySafe;
  const pregUnsafe = analysis.pregnancyUnsafeIngredients || [];
  const allergens = analysis.allergenFlags || [];
  const skin = analysis.skinTypeCompatibility || {};
  const parsed = analysis.parsedIngredients || [];
  const lowConf = analysis.flags?.lowConfidence;

  return (
    <div className="luxury-card p-4 space-y-4" data-testid="inci-analysis-panel">
      {/* Header row */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-luxury text-[#707070] mb-1">Analisi INCI</div>
          <div className="flex items-center gap-3 flex-wrap">
            <ProductStatus tone={LEVEL_TONE[level]} icon={ShieldCheck} testId="inci-analysis-safety-level">
              {LEVEL_LABEL[level] || level}
            </ProductStatus>
            <ProductStatus tone={pregSafe == null ? "neutral" : pregSafe ? "success" : "danger"} icon={Baby} testId="inci-analysis-pregnancy">
              {pregSafe == null ? "Gravidanza non valutata" : pregSafe ? "Sicuro in gravidanza" : "Sconsigliato in gravidanza"}
            </ProductStatus>
            {clean != null && clean >= 80 && (
              <ProductStatus tone="success" icon={Sparkles} testId="inci-analysis-clean">
                Clean beauty
              </ProductStatus>
            )}
            {allergens.length > 0 && (
              <ProductStatus tone="warning" icon={AlertTriangle} testId="inci-analysis-allergens-badge">
                {allergens.length} allerg.
              </ProductStatus>
            )}
            {lowConf && (
              <ProductStatus tone="warning" icon={Info} testId="inci-analysis-lowconf">
                Bassa confidenza
              </ProductStatus>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {updatedAt && (
            <span className="text-[10px] uppercase tracking-luxury text-[#707070]">
              Agg. {new Date(updatedAt).toLocaleString("it-IT")}
            </span>
          )}
          {onRefresh && (
            <button onClick={onRefresh} disabled={busy}
                    className="btn-ghost text-xs" data-testid="inci-analysis-refresh-btn">
              {busy ? "..." : "Rianalizza"}
            </button>
          )}
        </div>
      </div>

      {/* Scores grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t border-[#E5E5E0]">
        <Score label="Safety Score" value={overall} suffix="/10" color={levelColor} testId="inci-analysis-overall-score" />
        <Score label="Clean Beauty" value={clean} suffix="/100" color={clean >= 80 ? FIT_COLOR.good : FIT_COLOR.neutral} testId="inci-analysis-clean-score" />
        <Score label="Comedogenicità" value={comedo} suffix="/5" color={comedo && comedo >= 3 ? FIT_COLOR.bad : FIT_COLOR.good} testId="inci-analysis-comedo-score" />
        <Score label="Copertura DB" value={coverage != null ? Math.round(coverage * 100) : null} suffix="%" testId="inci-analysis-coverage" />
      </div>

      {/* Skin compatibility matrix */}
      {Object.keys(skin).length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-luxury text-[#707070] mb-2">Compatibilità pelle</div>
          <div className="flex flex-wrap gap-2" data-testid="inci-analysis-skin-matrix">
            {Object.entries(skin).map(([type, fit]) => (
              <div key={type} className="flex items-center gap-2 px-2.5 py-1 border border-[#E5E5E0] rounded-sm" data-testid={`inci-analysis-skin-${type}`}>
                <span className="text-xs">{SKIN_LABEL[type] || type}</span>
                <ProductStatus tone={FIT_TONE[fit]} testId={`inci-analysis-skin-${type}-status`}>
                  {FIT_LABEL[fit] || fit}
                </ProductStatus>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Allergens & pregnancy unsafe */}
      {(allergens.length > 0 || pregUnsafe.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          {allergens.length > 0 && (
            <div data-testid="inci-analysis-allergens-list">
              <div className="uppercase tracking-luxury text-[#707070] mb-1">Allergeni rilevati</div>
              <div className="text-[#8C3A3A]">{allergens.join(", ")}</div>
            </div>
          )}
          {pregUnsafe.length > 0 && (
            <div data-testid="inci-analysis-preg-unsafe-list">
              <div className="uppercase tracking-luxury text-[#707070] mb-1">Non sicuri in gravidanza</div>
              <div className="text-[#8C3A3A]">{pregUnsafe.join(", ")}</div>
            </div>
          )}
        </div>
      )}

      {/* Expand toggle */}
      {parsed.length > 0 && !compact && (
        <div>
          <button onClick={() => setExpanded(v => !v)}
                  className="btn-ghost text-xs inline-flex items-center gap-1.5"
                  data-testid="inci-analysis-expand-btn">
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? "Nascondi dettagli" : `Espandi dettagli (${parsed.length} ingredienti)`}
          </button>
          {expanded && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs border-t border-[#E5E5E0]" data-testid="inci-analysis-ingredients-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-luxury text-[#707070]">
                    <th className="py-2 text-left">Ingrediente</th>
                    <th className="text-left">Safety</th>
                    <th className="text-left">Livello</th>
                    <th className="text-left">Comedog.</th>
                    <th className="text-left">Irritante</th>
                    <th className="text-left">Allergene</th>
                    <th className="text-left">Gravidanza</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.map((r, i) => (
                    <tr key={i} className="border-t border-[#E5E5E0]" data-testid={`inci-analysis-row-${i}`}>
                      <td className="py-1.5 font-mono">{r.inciName}{!r.found && <span className="ml-1 text-[#B08D57]">*</span>}</td>
                      <td>{r.safetyScore ?? "—"}</td>
                      <td style={{ color: LEVEL_COLOR[r.safetyLevel] || "#1A1A1A" }} data-testid={`inci-analysis-ingredient-level-${i}`}>
                        {LEVEL_LABEL[r.safetyLevel] || r.safetyLevel || "—"}
                      </td>
                      <td>{r.comedogenicityRating ?? "—"}</td>
                      <td>{r.irritancyPotential || "—"}</td>
                      <td className={r.isAllergen ? "text-[#8C3A3A]" : ""}>
                        {r.isAllergen ? (r.allergenTypes || []).join(", ") || "Sì" : "No"}
                      </td>
                      <td className={r.pregnancySafe === false ? "text-[#8C3A3A]" : ""}>
                        {r.pregnancySafe == null ? "—" : r.pregnancySafe ? "Sì" : "No"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-[10px] text-[#707070] mt-2">* Ingrediente non trovato nel database di riferimento.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
